import {CustomSimpleErrorToast, CustomToast, CustomToastParams} from '@/components/compositions/custom-toast';
import {BrowserRouter} from 'react-router-dom';
import {toast} from 'sonner';

// Track shown toasts to prevent duplicates
const shownToasts = new Set<string>();
// Set a maximum lifetime of toasts in the tracking set (8 seconds)
const TOAST_TRACKING_LIFETIME = 8000;

/**
 * Generate a unique ID for a toast based on its content
 */
function generateToastId(params: CustomToastParams): string {
  // Use user-provided ID if available
  if (params.id) {
    return params.id;
  }
  
  // Use correlation ID as ID if available
  if (params.correlationId) {
    return `toast-${params.correlationId}`;
  }
  
  // Generate an ID based on content
  return `toast-${params.title}-${params.description?.substring(0, 20) || ''}-${Date.now()}`;
}

/**
 * Check if a toast with this ID has already been shown recently
 */
function isToastAlreadyShown(id: string): boolean {
  return shownToasts.has(id);
}

/**
 * Track that a toast has been shown and schedule its removal from tracking
 */
function trackToast(id: string, duration: number = TOAST_TRACKING_LIFETIME): void {
  shownToasts.add(id);
  
  // Remove from tracking after duration
  setTimeout(() => {
    shownToasts.delete(id);
  }, duration);
}

export function showToast(params: CustomToastParams) {
  const toastId = generateToastId(params);
  
  // Skip if already shown
  if (isToastAlreadyShown(toastId)) {
    console.log(`Skipping duplicate toast: ${toastId}`);
    return;
  }
  
  // Track this toast
  trackToast(toastId, params.duration || 5000);
  
  toast.custom((t) => (
    <BrowserRouter>
      <CustomToast params={{...params, toastId: t}} className={''} />
    </BrowserRouter>
  ), {
    duration: params.duration ? params.duration : 5000,
    dismissible: true,
    id: toastId
  });
}

export function showSimpleErrorToast(params: CustomToastParams) {
  const toastId = generateToastId(params);
  
  // Skip if already shown
  if (isToastAlreadyShown(toastId)) {
    console.log(`Skipping duplicate error toast: ${toastId}`);
    return;
  }
  
  // Track this toast
  trackToast(toastId, params.duration || 8000);
  
  // Log the toast parameters for debugging
  console.log('Showing error toast with params:', {
    title: params.title,
    description: params.description,
    correlationId: params.correlationId,
    id: toastId
  });
  
  toast.custom((t) =>
      <CustomSimpleErrorToast
        params={{
          ...params, 
          toastId: t
        }}
        className={''}
      />,
    {
      duration: params.duration ? params.duration : 8000, // Increase duration for error messages
      dismissible: true,
      id: toastId
    }
  );
}
